import { useState } from 'react';

/**
 * Hook para importar arquivos do microserviço de processamento
 * Determina automaticamente a URL:
 * - Se rodando local (localhost): usa http://localhost:3001
 * - Se rodando online: usa https://api.conferecartao.com.br
 */
export const getBaseUrl = () => {
    // Se estiver rodando localmente, usa o localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:3001';
    }
    // Caso contrário (produção), usa a URL do servidor online
    return 'https://api.conferecartao.com.br';
};

export function useFileImport(microserviceUrl = getBaseUrl()) {
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Importa um arquivo e retorna os dados processados pelo microserviço
     * @param {File} file - Arquivo a ser importado (CSV/XLS/XLSX)
     * @returns {Promise<{data: Array, metadata: Object}>}
     */
    const importFile = async (file, fileType = null) => {
        setIsImporting(true);
        setError(null);

        try {
            // Validar tipo de arquivo
            const validExtensions = ['.csv', '.xls', '.xlsx'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                throw new Error(`Formato de arquivo não suportado. Use: ${validExtensions.join(', ')}`);
            }

            // Criar FormData para enviar o arquivo
            const formData = new FormData();
            formData.append('file', file);
            if (fileType) {
                formData.append('fileType', fileType);
            }

            // Enviar para o microserviço
            const response = await fetch(`${microserviceUrl}/api/process`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Erro ao processar arquivo: ${response.statusText}`);
            }

            const result = await response.json();

            if (!result || !result.success) {
                throw new Error(result?.error || 'Erro desconhecido ao processar arquivo');
            }

            return {
                data: result.data || [],
                metadata: result.metadata || {},
                platformAdjustments: result.platformAdjustments || [],
            };

        } catch (err) {
            const errorMessage = err.message || 'Erro ao importar arquivo';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setIsImporting(false);
        }
    };

    return {
        importFile,
        isImporting,
        error,
    };
}
